import { Inject, Injectable, Injector, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, of, from } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { NotificationService } from '../../../core/firebase/notification.service';
import { ToastService } from '../toast/toast.service';
import { ApiService } from '../api/api.service';
import { encryptClientPayload, isClientEncryptedPayload } from '../../utils/client-payload-crypto.util';
import { getAuthPayloadEncryptionSecret } from '../../config/auth-payload-encryption';
import { encryptAuthTokenForStorage } from '../../utils/auth-token-storage.util';
import { maskSensitiveData } from '../../utils/sensitive-data-mask.util';

export interface AuthError {
  success: false;
  error: string;
}

export interface AuthSuccess {
  success: true;
}

export type AuthResult = AuthSuccess | AuthError;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly tokenKey = 'authToken';

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private toastService: ToastService,
    private apiService: ApiService,
    private injector: Injector,
  ) {}

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  /**
   * Register a new user via the FastAPI backend.
   */
  register(email: string, password: string, fullName?: string): Observable<AuthResult> {
    const endpoint = '/auth/register';
    return from(encryptClientPayload(password, getAuthPayloadEncryptionSecret())).pipe(
      switchMap((encryptedPassword) => {
        const payload = { email, password: encryptedPassword, full_name: fullName ?? null };
        // Pass only the endpoint path, not the full URL (ApiService will add baseUrl)
        return this.apiService.post(endpoint, payload, false);
      }),
      tap(() => {
        // Show success toast
        if (this.isBrowser()) {
          this.toastService.success('Account created successfully! Please login.', 'Success');
        }
      }),
      map(() => ({ success: true }) as AuthSuccess),
      catchError((error: any) => this.handleAuthError(error)),
    );
  }

  /**
   * Authenticate and store JWT locally.
   * Token is stored in localStorage for cross-tab session sharing.
   * Tokens are cleared on logout or when browser data is cleared.
   * For remembering user credentials, use the login component's rememberMe functionality
   * which only stores the email/username, never the password or token.
   */
  login(email: string, password: string): Observable<AuthResult> {
    const endpoint = '/auth/login';

    return from(encryptClientPayload(password, getAuthPayloadEncryptionSecret())).pipe(
      switchMap((encryptedPassword) => {
        const payload = { email, password: encryptedPassword };
        // Use centralized API service but disable auto-toast (we'll handle it manually)
        return this.apiService.post<any>(endpoint, payload, false);
      }),
      switchMap((response) => {
        const token = this.extractTokenFromResponse(response);
        if (!token || !this.isBrowser()) {
          if (!token) {
            console.warn('No token found in response:', maskSensitiveData(response));
          }
          return of(response);
        }
        return from(this.persistAccessToken(token)).pipe(
          tap(() => this.toastService.success('Login successful', 'Success')),
          map(() => response),
        );
      }),
      map((response) => {
        const hasToken = !!this.extractTokenFromResponse(response) || typeof response === 'string';
        if (hasToken) {
          return { success: true } as AuthSuccess;
        }
        return { success: false, error: 'Invalid response format from server' } as AuthError;
      }),
      catchError((error: any) => {
        // Don't log the full error as it might contain sensitive data
        // Only log the error status and message, not the full error object
        if (this.isBrowser()) {
          console.error('Login HTTP error:', {
            status: error?.status,
            message: error?.message || error?.error?.detail || 'Login failed',
          });
        }
        return this.handleAuthError(error);
      }),
    );
  }

  /**
   * Encrypt and store JWT in localStorage for cross-tab session sharing.
   * The encrypted value is sent to the API; the backend decrypts before JWT validation.
   */
  async persistAccessToken(plainToken: string): Promise<void> {
    if (!this.isBrowser() || !plainToken?.trim()) {
      return;
    }

    localStorage.removeItem(this.tokenKey);
    sessionStorage.removeItem(this.tokenKey);

    const encrypted = await encryptAuthTokenForStorage(plainToken);
    localStorage.setItem(this.tokenKey, encrypted);
  }

  private extractTokenFromResponse(response: any): string | null {
    if (response?.access_token) {
      return response.access_token;
    }
    if (response?.token) {
      return response.token;
    }
    if (response?.data?.token) {
      return response.data.token;
    }
    if (typeof response === 'string') {
      return response;
    }
    return null;
  }

  /**
   * Update token from response header (e.g., when token is refreshed).
   * This is called automatically by the auth interceptor when X-New-Access-Token header is present.
   */
  updateToken(newToken: string): void {
    if (newToken && this.isBrowser()) {
      void this.persistAccessToken(newToken);
    }
  }

  logout(): void {
    if (this.isBrowser()) {
      // Clear the authentication token from both storage locations
      localStorage.removeItem(this.tokenKey);
      sessionStorage.removeItem(this.tokenKey);
      // Also clear any other auth-related data
      localStorage.removeItem('user');
      sessionStorage.removeItem('user');
      this.clearFcmRegistrationOnLogout();
      // Note: We do NOT clear 'rememberedEmail' here - it should persist across logouts
      // so users only need to re-enter their password, not their email/username
    }
  }

  private clearFcmRegistrationOnLogout(): void {
    this.injector.get(NotificationService).clearRegistrationState();
  }

  isAuthenticated(): boolean {
    // Always check in browser environment to ensure token persistence
    if (!this.isBrowser()) {
      return false;
    }

    const token = this.getToken();
    if (!token) {
      return false;
    }

    // Encrypted transport tokens are validated by the backend on each request.
    if (isClientEncryptedPayload(token)) {
      return true;
    }

    // Legacy plain JWT / mock token checks
    try {
      // Try to decode the token to check if it's valid
      const decoded = JSON.parse(atob(token));
      // Check expiration if present
      if (decoded.exp && decoded.exp < Math.floor(Date.now() / 1000)) {
        // Token expired, remove it
        this.logout();
        return false;
      }
      return true;
    } catch (e) {
      // If it's a real JWT token, just check if it exists
      // Real tokens should be validated by the backend
      return !!token;
    }
  }

  /**
   * Return the stored bearer value (encrypted encv1 or legacy plain JWT) for API requests.
   */
  getToken(): string | null {
    if (!this.isBrowser()) {
      return null;
    }

    try {
      // Check both localStorage and sessionStorage
      // Priority: sessionStorage first (more recent), then localStorage
      const sessionToken = sessionStorage.getItem(this.tokenKey);
      if (sessionToken) {
        return sessionToken;
      }

      const localToken = localStorage.getItem(this.tokenKey);
      if (localToken) {
        return localToken;
      }

      return null;
    } catch (e) {
      // Handle cases where storage might not be accessible
      // (e.g., private browsing mode, storage disabled)
      console.warn('Error accessing storage:', maskSensitiveData(e));
      return null;
    }
  }

  private handleAuthError(error: any): Observable<AuthError> {
    let message = 'Something went wrong. Please try again.';

    // Handle error from centralized API service (wrapped error)
    const httpError = error?.error || error;
    const status = error?.status || httpError?.status || 0;

    if (status === 0) {
      // Backend is down or network error
      message = 'Something went wrong. Please try again.';
      if (this.isBrowser()) {
        this.toastService.error(message, 'Connection Error');
      }
    } else if (status === 401) {
      // Invalid credentials - show backend message
      message =
        httpError?.detail ||
        httpError?.error ||
        httpError?.message ||
        'Invalid email or password. Please try again.';
      if (this.isBrowser()) {
        this.toastService.error(message, 'Login Failed');
      }
    } else if (status === 422) {
      // Unprocessable Entity - Validation errors
      message = this.extractValidationErrorMessage(httpError);
      if (this.isBrowser()) {
        this.toastService.error(message, 'Validation Error');
      }
    } else if (status === 404) {
      message = 'Login endpoint not found. Please check the API configuration.';
      if (this.isBrowser()) {
        this.toastService.error(message, 'Error');
      }
    } else if (status >= 500) {
      message = 'Server error. Please try again later.';
      if (this.isBrowser()) {
        this.toastService.error(message, 'Server Error');
      }
    } else {
      // Other errors - show backend message
      message =
        httpError?.detail ||
        httpError?.error ||
        httpError?.message ||
        'Something went wrong. Please try again.';
      if (this.isBrowser()) {
        this.toastService.error(message, 'Error');
      }
    }

    // Log error without sensitive data
    if (this.isBrowser()) {
      console.error('Auth error:', {
        status,
        message,
        // Don't log the full error object as it might contain sensitive data
      });
    }
    return of({ success: false, error: message });
  }

  /**
   * Resend verification email for unverified users
   */
  resendVerificationEmail(email: string): Observable<{ success: boolean; message?: string }> {
    const endpoint = '/auth/resend-verification';
    const payload = { email };
    return this.apiService.post<{ success: boolean; message?: string }>(endpoint, payload, false).pipe(
      tap((response) => {
        if (this.isBrowser()) {
          this.toastService.success(
            response.message || 'Verification email sent successfully. Please check your inbox.',
            'Email Sent',
          );
        }
      }),
      map((response) => ({ success: response.success || true, message: response.message })),
      catchError((error: any) => {
        const httpError = error?.error || error;
        const message =
          httpError?.detail ||
          httpError?.error ||
          httpError?.message ||
          'Failed to send verification email. Please try again.';
        if (this.isBrowser()) {
          this.toastService.error(message, 'Error');
        }
        return of({ success: false, message });
      }),
    );
  }

  /**
   * Extract validation error message from 422 response
   * Handles different formats:
   * - Simple string: { detail: "error message" }
   * - Array of errors: { detail: [{ loc: ["field"], msg: "error", type: "error_type" }] }
   * - Object with error: { error: "error message" } or { message: "error message" }
   */
  private extractValidationErrorMessage(httpError: any): string {
    // Check if detail exists
    if (httpError?.detail) {
      const detail = httpError.detail;

      // If detail is a string, return it directly
      if (typeof detail === 'string') {
        return detail;
      }

      // If detail is an array (FastAPI validation errors format)
      if (Array.isArray(detail) && detail.length > 0) {
        // Extract messages from validation errors
        const messages = detail
          .map((err: any) => {
            // FastAPI format: { loc: ["field"], msg: "error", type: "error_type" }
            if (err?.msg) {
              const field =
                Array.isArray(err.loc) && err.loc.length > 1
                  ? err.loc[err.loc.length - 1]
                  : Array.isArray(err.loc) && err.loc.length > 0
                    ? err.loc[0]
                    : '';
              // Prefer the API message alone for body-level empty-field errors
              if (!field || field === 'body') {
                return err.msg;
              }
              return `${field}: ${err.msg}`;
            }
            // If it's a string in the array
            if (typeof err === 'string') {
              return err;
            }
            return null;
          })
          .filter((msg: string | null) => msg !== null);

        // Return combined messages or first message
        return messages.length > 0
          ? messages.join('. ')
          : 'Validation failed. Please check your input.';
      }

      // If detail is an object, try to extract message
      if (typeof detail === 'object') {
        return (
          detail.message ||
          detail.error ||
          detail.msg ||
          'Validation failed. Please check your input.'
        );
      }
    }

    // Fallback to other error fields
    if (httpError?.error) {
      if (typeof httpError.error === 'string') {
        return httpError.error;
      }
      if (Array.isArray(httpError.error) && httpError.error.length > 0) {
        return httpError.error.map((e: any) => e?.msg || e).join('. ');
      }
    }

    if (httpError?.message) {
      return httpError.message;
    }

    // Default message for 422
    return 'Validation failed. Please check your input and try again.';
  }
}
